import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../config/apiConfig";
import { AUTH_PROVIDER, storeAuthSession } from "../features/auth/session";
import axios from "axios";
import "./SSOCallbackPage.css";

export const SSOCallbackPage = ({ setIsLoggedIn }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing"); // processing | success | error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Estrai parametri dalla query string
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const sessionState = searchParams.get("session_state");

        // Validazione parametri
        if (!code || !state) {
          throw new Error("Parametri mancanti nel callback");
        }

        // Previeni esecuzioni multiple usando sessionStorage + global flag
        // Crea chiave unica basata su code (solo primi 20 caratteri per chiave più corta)
        const callbackKey = `sso_callback_${code.substring(0, 20)}`;
        
        // PRIMO CHECK: Global flag per bloccare race condition immediate
        if (window.__ssoCallbackInProgress === callbackKey) {
          console.log("🚫 Callback già in corso (global flag), abort");
          return;
        }
        
        if (sessionStorage.getItem(callbackKey) === "processed") {
          console.log("⏭️ Callback già elaborato per questo code, skip");
          // Se già processato con successo, redirect a homepage
          setIsLoggedIn(true);
          navigate("/", { replace: true });
          return;
        }

        // Marca immediatamente come in corso (global + storage)
        window.__ssoCallbackInProgress = callbackKey;
        sessionStorage.setItem(callbackKey, "processing");

        // Validazione CSRF state
        const savedState = sessionStorage.getItem("sso_state");
        if (!savedState || state !== savedState) {
          // Pulisci il flag se fallisce la validazione
          sessionStorage.removeItem(callbackKey);
          throw new Error("State non valido. Possibile attacco CSRF.");
        }

        console.log("✅ State validato, scambio code con token...");

        // Step 3: Scambia authorization code con token applicativi
        const response = await axios.get(`${API_BASE_URL}/oauth/sso/callback`, {
          params: {
            code,
            state,
            session_state: sessionState,
          },
        });

        const { access_token, refresh_token, user, is_new_user } = response.data;

        // Salva token in sessionStorage
        storeAuthSession({
          accessToken: access_token,
          refreshToken: refresh_token,
          user,
          provider: AUTH_PROVIDER.MICROSOFT_ENTRA_ID,
        });

        // Marca callback come processato con successo
        sessionStorage.setItem(callbackKey, "processed");
        delete window.__ssoCallbackInProgress; // Pulisci global flag

        // Rimuovi state SSO temporaneo
        sessionStorage.removeItem("sso_state");

        console.log("✅ Login SSO completato!", user);

        if (is_new_user) {
          console.log("🆕 Nuovo utente creato tramite SSO");
        }

        setStatus("success");

        // Attendi 1 secondo per mostrare successo, poi redirect
        setTimeout(() => {
          setIsLoggedIn(true);
          navigate("/", { replace: true });
        }, 1000);

      } catch (error) {
        console.error("❌ Errore SSO callback:", error);
        delete window.__ssoCallbackInProgress; // Pulisci global flag anche in caso di errore

        let message = "Errore durante l'autenticazione SSO.";

        if (error.response?.data?.detail) {
          message = error.response.data.detail;
        } else if (error.message) {
          message = error.message;
        }

        setErrorMessage(message);
        setStatus("error");

        // Rimuovi state temporaneo
        sessionStorage.removeItem("sso_state");

        // Redirect a login dopo 3 secondi
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setIsLoggedIn]);  return (
    <div className="sso-callback-container">
      {/* Background Image */}
      <img
        src={`${process.env.PUBLIC_URL}/background.jpeg`}
        alt="background"
        className="sso-callback-background"
      />

      {/* Overlay */}
      <div className="sso-callback-overlay"></div>

      {/* Content */}
      <div className="sso-callback-content">
        {status === "processing" && (
          <>
            <img
              className="sso-callback-spinner"
              src={`${process.env.PUBLIC_URL}/Trenitalia_logo_RDT.svg.png`}
              alt="loading"
            />
            <h2>Autenticazione in corso...</h2>
            <p>Attendere prego</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="sso-callback-success-icon">✓</div>
            <h2>Login completato!</h2>
            <p>Reindirizzamento alla homepage...</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="sso-callback-error-icon">✕</div>
            <h2>Errore di autenticazione</h2>
            <p>{errorMessage}</p>
            <p className="sso-callback-redirect-text">
              Reindirizzamento alla pagina di login...
            </p>
          </>
        )}
      </div>
    </div>
  );
};